import { CircularProgress } from '@material-ui/core';
import { useState } from 'react';
import toast from 'react-hot-toast';

import starIcon from '../../../common/images/staking/icon-star.svg';
import { Panel } from '../../common/panel/Panel.component';
import { Button } from '../../core/button';
import styles from './WithdrawStarsModal.module.css';

export interface WithdrawStarsModalProps {
  onClose: () => void;
  withdrawStars: (starsAmt: string) => Promise<boolean>;
  totalStarsEntered: string;
  totalRewards: string;
  rewardsPerBlock: number;
}

export const WithdrawStarsModal: React.FC<WithdrawStarsModalProps> = props => {
  const {
    onClose,
    withdrawStars,
    totalStarsEntered,
    totalRewards,
    rewardsPerBlock,
  } = props;

  const [starsAmount, setStarsAmount] = useState<string>('');
  const [overTotalError, setOverTotalError] = useState('');
  const [lostVotes, setLostVotes] = useState(0);

  const [submitting, setSubmitting] = useState(false);

  const updateStars = (value: string) => {
    const newAmount = parseFloat(value);
    setOverTotalError(
      newAmount > parseFloat(totalStarsEntered)
        ? 'insufficient STARS to withdraw'
        : '',
    );

    setStarsAmount(value);

    const votes =
      newAmount && newAmount <= parseFloat(totalStarsEntered)
        ? Math.floor(Math.sqrt(parseFloat(totalStarsEntered))) -
          Math.floor(Math.sqrt(parseFloat(totalStarsEntered) - newAmount))
        : 0;

    setLostVotes(votes);
  };

  const handleMax = () => {
    updateStars(totalStarsEntered);
  };

  const handleWithdraw = async () => {
    if (!starsAmount || Number.isNaN(starsAmount)) {
      return;
    }

    setSubmitting(true);
    const result = await withdrawStars(starsAmount);
    setSubmitting(false);

    if (result) {
      toast.success('Stars Withdrawn!');
    } else {
      toast.error('There was an error, try again later');
    }
    onClose();
    return result;
  };

  return (
    <Panel title="withdraw stars" hasExitModalButton={true} hideModal={onClose}>
      <div
        className={
          styles.container + ' flex flex-col flex-grow items-center mt-4 mx-4'
        }
      >
        <div className="relative flex flex-row items-center w-full">
          <input
            type="number"
            placeholder="0"
            value={starsAmount}
            onChange={(e: { target: { value: string } }) =>
              updateStars(e.target.value)
            }
            className={
              styles.votingInput +
              ' h-14 border-0 rounded-l-lg outline-none text-right px-4 w-full'
            }
          />
          <button
            className={
              styles.maxButton +
              ' h-14 px-4 text-center flex flex-row items-center rounded-r-lg text-primary uppercase font-bold'
            }
            onClick={handleMax}
          >
            <span>max</span>
          </button>
          <div className="absolute top-3 left-4 flex flex-row items-center">
            <div className="grid place-items-center w-8 h-8 bg-white bg-opacity-5 rounded-full">
              <img src={starIcon} alt="stars icon" />
            </div>
            <div className="ml-2 font-noto-sans font-bold text-lg">STARS</div>
          </div>
        </div>
        <div className="h-6 w-full text-red-500">{overTotalError}</div>

        <div className="flex flex-row w-full mt-2 uppercase font-bold font-noto-sans">
          Stars Voted
        </div>

        <div className="flex flex-row justify-between w-full mt-6 font-noto-sans">
          <div className="text-secondary">Your stars</div>
          <div>
            {parseFloat(totalStarsEntered).toLocaleString('en-us', {
              maximumFractionDigits: 3,
              useGrouping: true,
            })}{' '}
            STARS
          </div>
        </div>

        {rewardsPerBlock > Math.pow(1, -19) && (
          <>
            <div className="flex flex-row justify-between w-full mt-6 font-noto-sans">
              <div className="text-secondary">Your rewards</div>
              <div>
                {parseFloat(totalRewards).toLocaleString('en-us', {
                  maximumFractionDigits: 3,
                  useGrouping: true,
                })}{' '}
                STARS
              </div>
            </div>

            <div className="border-t border-gray opacity-10 mx-6 mt-8 w-full"></div>

            <div className="flex flex-row w-full mt-8 uppercase font-bold font-noto-sans">
              lost rewards
            </div>

            <div className="flex flex-row justify-between w-full mt-6 font-noto-sans">
              <div className="text-secondary">Lost votes</div>
              <div>
                {lostVotes.toLocaleString('en-us', {
                  maximumFractionDigits: 3,
                  useGrouping: true,
                })}{' '}
                VOTES
              </div>
            </div>

            <div className="mt-6 text-noto-sans">
              <span className="text-primary">Warning:</span> Removing STARS
              before the voting round ends will result in losing your
              accumulated rewards.
            </div>
          </>
        )}

        <Button
          className="mt-8 w-full"
          disabled={
            !!overTotalError ||
            !starsAmount ||
            Number.isNaN(starsAmount) ||
            submitting
          }
          onClick={handleWithdraw}
        >
          {submitting ? (
            <CircularProgress style={{ color: 'white' }} />
          ) : (
            'withdraw'
          )}
        </Button>
      </div>
    </Panel>
  );
};

export const createWithdrawStarsModal =
  ({
    totalStarsEntered,
    totalRewards,
    rewardsPerBlock,
    withdrawStars,
  }: {
    totalStarsEntered: string;
    totalRewards: string;
    rewardsPerBlock: number;
    withdrawStars: (starsAmt: string) => Promise<boolean>;
  }) =>
  ({ hideModal }: { hideModal: () => void }) => {
    return (
      <WithdrawStarsModal
        onClose={hideModal}
        totalStarsEntered={totalStarsEntered}
        totalRewards={totalRewards}
        rewardsPerBlock={rewardsPerBlock}
        withdrawStars={withdrawStars}
      />
    );
  };
